import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { AiOutlineEdit } from "react-icons/ai";
import { BsPlus } from "react-icons/bs";
import { FiCornerDownRight } from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { MdOutlineExpandMore, MdOutlineKeyboardArrowRight } from "react-icons/md";
import { Loading } from "../../../../components";
import Confirm from "../../../../components/confirm/Confirm";
import EditableControl from "../../../../components/editable-text/EditableText";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import FieldTypeValueDisplay, { DEFAULT_VALUE_REPLACEMENTS } from "../../../../components/form-field/FieldTypeValueDisplay";
import FormFieldDisplay from "../../../../components/form-field/FormField";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import FieldType, { CUSTOM_FIELD_PROPS } from "../../../../components/pickers/field-type/FieldType";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Select from "../../../../components/select/Select";
import useRouting, { useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/UserContext";

import {
    CustomField as ApiCustomField,
    FieldTypedReplaceableValueInput,
    FieldTypedValueBaseInput,
    FormField,
    FormFieldDescriptorInput,
    FormFieldOption,
    FormFieldReplacementInput,
    FormFieldType,
    FormFieldVisibility,
    GetFormDataDocument,
    GetFormFieldDocument,
    useDeleteFormFieldMutation,
    useGetFormFieldLazyQuery,
    useSaveFormFieldMutation,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";
import Styles from "./CustomField.module.scss";

const VISBILITY_OPTIONS = [
    {
        label: "Reusable",
        value: FormFieldVisibility.Reusable,
    },
    {
        label: "Private",
        value: FormFieldVisibility.Private,
    },
];

interface OptionProps {
    option: FormFieldOption;
    allowedLevels: number;
    level: number;
    levels: string[];
    onUpdated: () => void;
    canEditLabels: boolean;
    onDelete: () => void;
}
const BUTTON_CLASS = "btn btn-link p-0 ms-3 text-light";
function Option({
    option, level, allowedLevels, levels, onUpdated, canEditLabels,
    onDelete,
}: OptionProps): ReactElement {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const hasChildren = option.children?.isNotEmpty;
    const canHaveChildren = allowedLevels === -1 || level < allowedLevels;
    const addChild = () => {
        // eslint-disable-next-line no-param-reassign
        option.children = option.children ?? [];
        option.children.push({
            label: "",
            description: "",
        });
        setIsExpanded(true);
        onUpdated();
    };
    const changeLabel = (value: string) => {
        // eslint-disable-next-line no-param-reassign
        option.label = value;
        // eslint-disable-next-line no-param-reassign
        option.value = value;
        setIsEditing(false);
        onUpdated();
    };
    const changeDescription = (value: string) => {
        // eslint-disable-next-line no-param-reassign
        option.description = value;
        setIsEditing(false);
        onUpdated();
    };
    return (
        <li className="">
            <div className="d-flex align-items-start list-group-item list-border border-top-0 border-end-0 mt-2">
                {canHaveChildren && hasChildren && (
                    <button
                        type="button"
                        className="btn btn-sm btn-none p-0 me-2"
                        onClick={() => setIsExpanded(current => !current)}
                    >
                        {isExpanded ? <MdOutlineExpandMore /> : <MdOutlineKeyboardArrowRight />}
                    </button>
                )}
                {canHaveChildren && !hasChildren && (
                    <span className="ms-4" />
                )}
                <div className="d-flex flex-column flex-grow-1">
                    <div className="d-flex justify-content-between w-100">
                        <div>
                            <EditableControl
                                value={option.label}
                                onChange={value => changeLabel(value as string)}
                                isEditing={isEditing}
                            />
                        </div>

                        <div className="text-light ">
                            {canHaveChildren && (
                                <button
                                    type="button"
                                    className={BUTTON_CLASS}
                                    title="Add a child choice to the current choice."
                                    onClick={addChild}
                                >
                                    <FiCornerDownRight />

                                </button>
                            )}
                            <button
                                type="button"
                                className={BUTTON_CLASS}
                                title="Edit current option label or description"
                                onClick={() => setIsEditing(true)}
                            >
                                <AiOutlineEdit />
                            </button>

                            <button
                                type="button"
                                className={BUTTON_CLASS}
                                title="Delete the current node"
                                onClick={onDelete}
                            >
                                <IoCloseSharp />
                            </button>
                        </div>

                    </div>

                    <div>
                        <small>
                            <EditableControl
                                value={option.description ?? ""}
                                onChange={value => changeDescription(value as string)}
                                placeholder="Enter a description"
                                isEditing={isEditing}
                                shouldFocus={false}
                                isFormField
                            />
                        </small>
                    </div>
                </div>

            </div>

            {(canHaveChildren && option.children?.isNotEmpty && option.children && isExpanded) && (
                <div className="ms-4">
                    {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                    <Options
                        options={option.children ?? []}
                        level={level + 1}
                        allowedLevels={allowedLevels}
                        levels={levels}
                        onUpdated={onUpdated}
                        canEditLabels={canEditLabels}
                    />
                </div>
            )}
        </li>
    );
}

function flattenOptions(options: FormFieldOption[], spaces: string): string {
    return options.map(row => {
        const children = row.children?.isNotEmpty ? `\n${flattenOptions(row.children, `${spaces}-`)}` : "";

        const line = `${spaces}${row.label}${row.description ? ` - ${row.description}` : ""}`.trim();
        return `${line}${children}`.trim();
    }).join("\n");
}

function optionsFromString(lines: string[]): FormFieldOption[] {
    const options: FormFieldOption[] = [];
    let last: FormFieldOption | undefined;
    let buffer: string[] = [];
    lines.forEach(line => {
        if (line.trim() === "") {
            return;
        }
        if (line.startsWith("-")) {
            buffer.push(line.substring(1));
        } else {
            if (last) {
                last.children = buffer.isNotEmpty ? optionsFromString(buffer).limit(100) : undefined;
            }
            buffer = [];
            const parts = line.split("-");
            last = {
                label: parts[0].trim(),
                value: parts[0].trim(),
                description: parts.skip(1).join("-").trim(),
            };
            options.push(last);
        }
    });
    if (last) {
        last.children = buffer.isNotEmpty ? optionsFromString(buffer).limit(100) : undefined;
    }
    return options;
}

interface OptionsProps {
    options: FormFieldOption[];
    canEditLabels: boolean;
    level: number;
    allowedLevels: number;
    levels: string[];
    onUpdated: () => void;
}
function Options({
    options, level, allowedLevels, levels, onUpdated, canEditLabels,
}: OptionsProps): ReactElement {
    const [isEditAll, setEditAll] = useState(false);
    const [bulkText, setBulkText] = useState("");

    const levelUpdate = (value: string) => {
        // eslint-disable-next-line no-param-reassign
        levels[level - 1] = value;
        onUpdated();
    };
    const addLevel = () => {
        options.push({
            label: "",
            description: "",
        });
        onUpdated();
    };
    const startEdit = () => {
        setBulkText(flattenOptions(options, ""));
        setEditAll(true);
    };
    const saveBulk = () => {
        const childOptions = optionsFromString(bulkText?.split("\n") ?? []);
        options.splice(0, options.length, ...childOptions);
        onUpdated();
        setEditAll(false);
    };
    const onDelete = (index: number) => {
        options.splice(index, 1);
        onUpdated();
    };
    if (isEditAll) {
        return (
            <div>
                <textarea
                    className={`w-100 align-self-stretch form-control ${Styles["bulk-edit"]}`}
                    value={bulkText}
                    onChange={event => setBulkText(event.target.value)}
                />
                <small className="text-light">
                    Enter 1 value per line, max 100 per level. To add a description, add a
                    {" "}
                    <code>-</code>
                    {" "}
                    in the beginning of the line.
                </small>
                <div className="d-flex justify-content-end mt-2">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => setEditAll(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={saveBulk}>Done</button>
                </div>
            </div>
        );
    }
    return (
        <ul className="list-unstyled">
            {canEditLabels && (
                <li className="mt-2 fs-5 text-primary">
                    <EditableControl
                        value={levels[level - 1]}
                        onChange={value => levelUpdate(value as string)}
                        placeholder="Enter a label for this level"
                    />
                </li>
            )}

            {options?.map((option, index) => (
                <Option
                    option={option}
                    key={option.value}
                    level={level}
                    allowedLevels={allowedLevels}
                    levels={levels}
                    onUpdated={onUpdated}
                    canEditLabels={canEditLabels}
                    onDelete={() => onDelete(index)}
                />
            ))}
            <li className="d-flex justify-content-end">
                <button type="button" className="btn btn-link p-0 pe-auto me-4" onClick={addLevel}>
                    <small className="text-light">Add</small>
                </button>
                <button type="button" className="btn btn-link p-0 pe-auto" onClick={startEdit}>
                    <small className="text-light">Bulk Edit</small>
                </button>
            </li>

        </ul>

    );
}

function copyOptions(options: FormFieldOption[]): FormFieldOption[] {
    return options.map(row => ({
        ...row,
        children: row.children ? copyOptions(row.children) : undefined,
        __typename: undefined,
    }));
}

function filterEmptyOptions(options: FormFieldOption[]): FormFieldOption[] {
    return options.mapFilter(row => (row.label ? {
        ...row,
        children: row.children ? filterEmptyOptions(row.children) : undefined,
    } : undefined)) as FormFieldOption[];
}

const DEFAULT_FIELD_OPTION: FormFieldOption = {
    label: "",
    description: "",
};

const CHOICE_TYPE = new Set([
    FormFieldType.Checkboxes,
    FormFieldType.Dropdown,
    FormFieldType.MultiSelectDropdown,
    FormFieldType.MultiLevelDropdown,
]);
export interface CustomFieldProps {
    id: string;
    onDone: (id?: string, field?: FormFieldDescriptorInput) => void;
    shouldPreview: boolean;
    isModal: boolean;
    onClose?: () => void;
    canSetPrivate?: boolean;
    groupIds?: string[];
    canDelete?: boolean;
    formField?: FormFieldDescriptorInput;
    onTypeSelected?: (type: FormFieldType) => void;
    requestFormType?: string | undefined;
}
export function CustomFieldDialog({
    id, onDone, shouldPreview, isModal, onClose, canSetPrivate, groupIds, formField, canDelete, onTypeSelected, requestFormType,
}: CustomFieldProps): ReactElement {
    const userSession = useCurrentSession();

    const [loadField, { data, error, loading: isLoading }] = useGetFormFieldLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [saveFormField] = useSaveFormFieldMutation();
    const [deleteFormField] = useDeleteFormFieldMutation();

    const nameRef = React.createRef<HTMLInputElement>();

    const [name, setName] = useState<string>(() => data?.formField?.name ?? "");
    const [placeholder, setPlaceholder] = useState<string>(() => data?.formField?.placeholder ?? "");

    const [description, setDescription] = useState<string>(() => data?.formField?.description ?? "");
    const [type, setType] = useState(() => data?.formField?.type ?? FormFieldType.Text);
    const [visibility, setVisibility] = useState(() => data?.formField?.visibility
        ?? (canSetPrivate ? FormFieldVisibility.Private : FormFieldVisibility.Reusable));

    const [isRequired, setIsRequired] = useState<boolean>(formField?.isRequired ?? false);
    const [isRequiredForAgent, setIsRequiredForAgent] = useState<boolean>(formField?.isRequiredForAgent ?? false);

    const [defaultValue, setDefaultValue] = useState<FieldTypedReplaceableValueInput | undefined>(
        formField?.defaultValue as FieldTypedReplaceableValueInput,
    );

    useEffect(() => {
        setIsRequired(formField?.isRequired ?? false);
        setIsRequiredForAgent(formField?.isRequiredForAgent ?? false);
    }, [formField]);

    useEffect(() => {
        onTypeSelected?.(type);
    }, [type]);

    const [options, setOptions] = useState(() => data?.formField?.options ?? (id === "new" ? [
        { ...DEFAULT_FIELD_OPTION },
        { ...DEFAULT_FIELD_OPTION },
    ] : []));
    const [levels, setLevels] = useState(() => data?.formField?.levels ?? []);
    const [displayDelete, setDisplayDelete] = useState(false);
    const [isInvalidChoices, setIsInvalidChoices] = useState(false);
    const [previewValue, setPreviewValue] = useState<ApiCustomField>({
        formFieldId: "",
    });
    const [userGroupIds, setUserGroupIds] = useState(() => data?.formField?.userGroupIds as string[]
        ?? userSession.user?.userGroups?.toIdList("id")
        ?? []);

    useEffect(() => {
        if (!data) {
            return;
        }
        setName(data?.formField?.name ?? "");
        setPlaceholder(data?.formField?.placeholder ?? "");
        setDescription(data?.formField?.description ?? "");
        setUserGroupIds(data?.formField?.userGroupIds ?? []);
        setType(data?.formField?.type ?? FormFieldType.Text);
        setOptions(copyOptions(data?.formField?.options ?? []));
        setLevels([...data?.formField?.levels ?? []]);
        setVisibility(data?.formField?.visibility
            ?? (canSetPrivate ? FormFieldVisibility.Private : FormFieldVisibility.Reusable));
    }, [data?.formField]);

    useEffect(() => {
        setIsInvalidChoices(false);
    }, [options]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }
    if (id !== "new" && !data?.formField) {
        loadField({
            variables: {
                id,
            },
        });
    }

    const fieldProps = CUSTOM_FIELD_PROPS[type];

    const currentField: Omit<FormField, "id" | "isSystemField"> = {
        name,
        description,
        placeholder,
        options: filterEmptyOptions(options),
        levels,
        type,
        userGroupIds,
        visibility,
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (CHOICE_TYPE.has(type) && (options.isEmpty || options.some(option => !option.label))) {
            setIsInvalidChoices(true);
            return;
        }

        if (!nameRef.current?.checkValidity()) {
            nameRef.current?.reportValidity();
            return;
        }
        (async () => {
            const createdField = await saveFormField({
                refetchQueries: [
                    { query: GetFormFieldDocument },
                    { query: GetFormDataDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    formField: currentField,
                },
            });
            onDone(createdField.data?.saveFormField.id, {
                fieldId: createdField.data?.saveFormField.id,
                isRequired,
                isRequiredForAgent,
                defaultValue,
            });
        })();
    };

    const onDelete = async () => {
        await deleteFormField({
            refetchQueries: [
                { query: GetFormFieldDocument },
            ],
            variables: {
                id,
            },
        });
        onDone();
    };

    const selectType = async (selected: FormFieldType) => {
        if (selected === type) {
            return;
        }
        setType(selected);
        setPreviewValue({
            formFieldId: "",
        });
    };

    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the field:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>
            <div>
                {onClose && (
                    <div className="d-flex justify-content-between p-2">
                        {id === "new" ? <h5>Create a new field</h5> : <h5>Edit field</h5>}
                        <button type="button" className="btn-close btn-none m-1" onClick={onClose} aria-label="close" />
                    </div>
                )}
                <div className={isModal ? "d-flex" : ""}>
                    <div className={`d-flex bg-white rounded ${isModal ? "px-3 col" : "border mb-4 p-3 "}`}>
                        <div className="col">
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Name</Form.Label>
                                <Form.Control
                                    ref={nameRef}
                                    type="text"
                                    placeholder="Name"
                                    required
                                    onChange={evt => setName(evt.target.value)}
                                    value={name}
                                />
                                <Form.Text>
                                    The name of the field displayed at the top.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Type</Form.Label>

                                <FieldType
                                    className="text-gray"
                                    onChange={value => selectType(value)}
                                    value={type}
                                />
                                <Form.Text>
                                    What is the type of data you want to store in this field? Text? A number? A choice?
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">
                                    Description
                                    {" "}
                                    <small className="text-light">(Optional)</small>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    as="textarea"
                                    placeholder="This form field..."
                                    onChange={evt => setDescription(evt.target.value)}
                                    value={description}
                                />
                                <Form.Text>
                                    The optional description or helpful hints to add to the bottom of the page.
                                </Form.Text>
                            </Form.Group>
                            {canSetPrivate && (
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">
                                        Required
                                        {" "}
                                        <small className="text-light">(Optional)</small>
                                    </Form.Label>
                                    <BooleanSelect
                                        value={isRequired}
                                        isRequired
                                        onChange={value => setIsRequired(value)}
                                    />
                                    <Form.Text>
                                        Is this field required to be entered when creating?
                                    </Form.Text>
                                </Form.Group>
                            )}
                            <ExpandingSection title="Advanced Settings" classBackground="bg-gray-100">
                                <>
                                    {requestFormType !== "action" ? (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bolder">
                                                Required for close
                                            </Form.Label>
                                            <BooleanSelect
                                                value={isRequiredForAgent}
                                                isRequired
                                                onChange={value => setIsRequiredForAgent(value ?? false)}
                                            />
                                            <Form.Text>
                                                Does the agent need to complete this field before the ticket can be closed?
                                            </Form.Text>
                                        </Form.Group>
                                    ) : <span />}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bolder">
                                            Example
                                            {" "}
                                            <small className="text-light">(Optional)</small>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Example text"
                                            onChange={evt => setPlaceholder(evt.target.value)}
                                            value={placeholder}
                                        />
                                        <Form.Text>
                                            This text appears in the text by default. This is usually an example of what
                                            the user should enter.
                                        </Form.Text>
                                    </Form.Group>

                                    {canSetPrivate && currentField && (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bolder">
                                                Default Value
                                            </Form.Label>
                                            <FieldTypeValueDisplay
                                                shouldShowFieldName={false}
                                                field={{ ...currentField, isRequired: false } as FormField}
                                                value={defaultValue?.value as FieldTypedValueBaseInput}
                                                onChange={(value, replacements) => setDefaultValue({ value, replacements })}
                                                replacements={defaultValue?.replacements as FormFieldReplacementInput[]}
                                                replaceableInput={currentField.type === FormFieldType.Text
                                                    || currentField.type === FormFieldType.LongText
                                                    ? undefined : DEFAULT_VALUE_REPLACEMENTS}
                                            />
                                            <Form.Text>
                                                Set a default value for the field or no value if not required.
                                            </Form.Text>
                                        </Form.Group>
                                    )}

                                    {!groupIds && (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bolder">
                                                Groups
                                            </Form.Label>
                                            <UserGroups
                                                isMulti
                                                value={userGroupIds}
                                                onSelected={ids => setUserGroupIds(ids)}
                                            />
                                            <Form.Text>
                                                Which user groups are allowed to access this custom field?
                                            </Form.Text>
                                        </Form.Group>
                                    )}

                                    {canSetPrivate && (
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-bolder">Visibility</Form.Label>

                                            <Select
                                                className="text-gray"
                                                placeholder=""
                                                onChange={value => setVisibility(value.value as FormFieldVisibility)}
                                                options={VISBILITY_OPTIONS}
                                                value={VISBILITY_OPTIONS.find(option => option.value === visibility)}
                                            />
                                            <Form.Text>
                                                This field is only visible to the current request type.
                                            </Form.Text>
                                        </Form.Group>
                                    )}
                                </>
                            </ExpandingSection>

                        </div>
                        {shouldPreview && (
                            <div className="col-4 border-start ms-3 ps-3">
                                <div className=" border bg-gray-100 p-3 rounded">
                                    <h5 className="text-light">Preview</h5>

                                    <Form.Group className="mt-3">
                                        <Form.Label className="fw-bolder text-gray-600">
                                            {name}
                                        </Form.Label>
                                        <FormFieldDisplay
                                            formField={currentField as FormField}
                                            isEditable
                                            isEditing
                                            shouldFocus={false}
                                            value={previewValue}
                                            onChange={val => setPreviewValue({
                                                formFieldId: "",
                                                value: val.customField ?? {
                                                    formFieldId: "",
                                                },
                                            })}
                                            isFormField
                                        />
                                        <Form.Text>
                                            {description}
                                        </Form.Text>
                                    </Form.Group>

                                </div>
                                <small className="text-light mt-3">
                                    The preview above gives you an idea of what the custom field will look like
                                    and how it will function on a page.
                                </small>

                            </div>
                        )}

                    </div>

                    {fieldProps?.canEnterChoice && (
                        <div className={`p-3 rounded ${isModal ? "col pt-0" : "mb-4 border"} bg-white`}>

                            <h5 className="mb-2 text-light">
                                Choices
                            </h5>
                            <div className="d-flex align-items-start">
                                <div className="col px-3 border-end">
                                    {isInvalidChoices && (
                                        <div className="mt-2 p-2 rounded alert alert-danger">
                                            This field type is for selecting from a set of choices.
                                            You can not create this field type without at least 1 option.
                                            Please enter one or more choices below.
                                        </div>
                                    )}
                                    <Options
                                        options={options}
                                        level={1}
                                        allowedLevels={fieldProps.childLevels}
                                        levels={fieldProps.labels ?? levels}
                                        canEditLabels={fieldProps.labels == null}
                                        onUpdated={() => {
                                            setOptions([...options]);
                                        }}
                                    />
                                </div>
                                {shouldPreview && (
                                    <div className="col-4 px-3">

                                        <div className="text-light">
                                            <h5 className="text-light">Adding choices</h5>
                                            {type === FormFieldType.MultiLevelDropdown && (
                                                <>
                                                    <p>
                                                        A Multi-level choice allows you to chose multiple levels
                                                        of items. Choosing one
                                                        level will optionally display the next level of items to choose from.
                                                    </p>
                                                    <p>
                                                        For example, imagine you want a user to select a country, state, and city.
                                                        In the first level, the user will be prompted to select
                                                        a country from a list of
                                                        configured countries. Once selected, the user will be
                                                        prompted to select a state
                                                        from the list of configured states belonging to the country selected in
                                                        the previous step. Finally the user will be prompted to choose a city
                                                        from the configured set of cities belonging to the state selected in
                                                        the previous step.
                                                    </p>
                                                </>
                                            )}
                                            {CHOICE_TYPE.has(type)
                                                && (
                                                    <>
                                                        <p>
                                                            Adding choices to the list on the left will allow them
                                                            to be selected in the
                                                            {" "}
                                                            {fieldProps.text}
                                                            {" "}
                                                            list.
                                                        </p>
                                                        <p>
                                                            You may also specify categories for the list.
                                                            Categories are not selectable, but are to help organize
                                                            and group similar items together.
                                                        </p>
                                                    </>
                                                )}

                                            <h5 className="text-light mt-4 d-block">Controls</h5>
                                            <dl>
                                                <dt>Bulk edit</dt>
                                                <dd>
                                                    Allows you to edit all choices and all of the children of those
                                                    choices in bulk using only text.

                                                </dd>
                                                <dt>Add</dt>
                                                <dd>Adds a new row in the same list as the link</dd>

                                                <dt className="mt-3"><BsPlus className="fs-4" /></dt>
                                                <dd>Adds a new row as a child of the current row.</dd>

                                                <dt><AiOutlineEdit className="fs-4" /></dt>
                                                <dd>
                                                    Starts editing the current row. You can also modify
                                                    the rows labels and descriptions by clicking on them.
                                                </dd>

                                                <dt><IoCloseSharp className="fs-4" /></dt>
                                                <dd>Deletes the current row and all of its children</dd>
                                            </dl>

                                        </div>

                                    </div>
                                )}

                            </div>
                        </div>
                    )}

                </div>
            </div>
            <div className={`d-flex justify-content-end ${isModal ? "" : "mt-3 fixed-bottom border-top"} p-3 bg-white`}>
                {id !== "new" && canDelete !== false && (
                    <button type="button" className="btn btn-link text-danger me-4" onClick={() => setDisplayDelete(true)}>
                        Delete
                    </button>
                )}

                <button type="submit" className="btn btn-primary" onClick={onSubmit}>
                    Save
                </button>
            </div>
        </>
    );
}

CustomFieldDialog.defaultProps = {
    onClose: undefined,
    canSetPrivate: undefined,
    groupIds: undefined,
    canDelete: undefined,
    formField: undefined,
    onTypeSelected: undefined,
    requestFormType: undefined,
};

export default function CustomField(): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const onDone = () => {
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "request-types",
        });
    };
    return <CustomFieldDialog id={id} onDone={onDone} shouldPreview isModal={false} />;
}

export function CustomFieldModal({
    id, onDone, shouldPreview, onClose, canSetPrivate, groupIds, formField, canDelete, requestFormType,
}: Omit<CustomFieldProps, "isModal">): ReactElement {
    const [type, setType] = useState(FormFieldType.Text);
    return (
        <Confirm
            shouldShow={id != null}
            size={CHOICE_TYPE.has(type) ? "xl" : "lg"}
            bodyClassName="h-100 w-100 m-0 p-0 d-flex flex-column"
            dialogClassName="card bg-white shadow border-0"
            className="h-100 border-0"
            fullscreen="lg-down"
        >
            <CustomFieldDialog
                id={id}
                formField={formField}
                canSetPrivate={canSetPrivate}
                canDelete={canDelete}
                groupIds={groupIds}
                onDone={onDone}
                shouldPreview={shouldPreview}
                isModal
                onClose={onClose}
                onTypeSelected={setType}
                requestFormType={requestFormType}
            />
        </Confirm>
    );
}

CustomFieldModal.defaultProps = {
    onClose: undefined,
    canSetPrivate: undefined,
    groupIds: undefined,
    canDelete: undefined,
    formField: undefined,
    onTypeSelected: undefined,
};
